import React from 'react';
import { useTranslation } from 'react-i18next';
import './AboutUs.css';

const About = () => {
  const { t } = useTranslation('common');

  return (
    <div className="about-container">
      {/* Hero Section */}
      <div className="hero-section">
        <h1 className="hero-title">{t('about.hero.title')}</h1>
        <p className="hero-subtitle">{t('about.hero.subtitle')}</p>
        <div className="hero-tagline">
          <span className="tagline-text">{t('about.hero.tagline')}</span>
        </div>
      </div>

      {/* Mission Section */}
      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">{t('about.mission.title')}</h2>
        </div>
        <div className="mission-content">
          <div className="mission-text">
            <p className="mission-description">
              {t('about.mission.description')}
            </p>
            <p className="mission-vision">
              {t('about.mission.vision')}
            </p>
          </div>
          <div className="mission-values">
            <h3>{t('about.mission.values_title')}</h3>
            <div className="values-grid">
              <div className="value-item">
                <span className="value-icon">🌍</span>
                <h4>{t('about.values.sustainability.title')}</h4>
                <p>{t('about.values.sustainability.description')}</p>
              </div>
              <div className="value-item">
                <span className="value-icon">🤝</span>
                <h4>{t('about.values.community.title')}</h4>
                <p>{t('about.values.community.description')}</p>
              </div>
              <div className="value-item">
                <span className="value-icon">🔒</span>
                <h4>{t('about.values.safety.title')}</h4>
                <p>{t('about.values.safety.description')}</p>
              </div>
              <div className="value-item">
                <span className="value-icon">💡</span>
                <h4>{t('about.values.innovation.title')}</h4>
                <p>{t('about.values.innovation.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">{t('about.features.title')}</h2>
          <p className="section-subtitle">{t('about.features.subtitle')}</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🚗</div>
            <h3>{t('about.features.ridesharing.title')}</h3>
            <p>{t('about.features.ridesharing.description')}</p>
            <ul className="feature-benefits">
              <li>{t('about.features.ridesharing.benefit1')}</li>
              <li>{t('about.features.ridesharing.benefit2')}</li>
              <li>{t('about.features.ridesharing.benefit3')}</li>
            </ul>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📦</div>
            <h3>{t('about.features.delivery.title')}</h3>
            <p>{t('about.features.delivery.description')}</p>
            <ul className="feature-benefits">
              <li>{t('about.features.delivery.benefit1')}</li>
              <li>{t('about.features.delivery.benefit2')}</li>
              <li>{t('about.features.delivery.benefit3')}</li>
            </ul>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🌐</div>
            <h3>{t('about.features.community.title')}</h3>
            <p>{t('about.features.community.description')}</p>
            <ul className="feature-benefits">
              <li>{t('about.features.community.benefit1')}</li>
              <li>{t('about.features.community.benefit2')}</li>
              <li>{t('about.features.community.benefit3')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">{t('about.howItWorks.title')}</h2>
        </div>
        <div className="how-it-works">
          <div className="steps-container">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>{t('about.howItWorks.step1.title')}</h3>
                <p>{t('about.howItWorks.step1.description')}</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>{t('about.howItWorks.step2.title')}</h3>
                <p>{t('about.howItWorks.step2.description')}</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>{t('about.howItWorks.step3.title')}</h3>
                <p>{t('about.howItWorks.step3.description')}</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>{t('about.howItWorks.step4.title')}</h3>
                <p>{t('about.howItWorks.step4.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Impact Section */}
      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">{t('about.impact.title')}</h2>
          <p className="section-subtitle">{t('about.impact.subtitle')}</p>
        </div>
        <div className="impact-stats">
          <div className="stat-item">
            <span className="stat-icon">🌱</span>
            <h3>{t('about.impact.environment.title')}</h3>
            <p>{t('about.impact.environment.description')}</p>
          </div>
          
          <div className="stat-item">
            <span className="stat-icon">💰</span>
            <h3>{t('about.impact.savings.title')}</h3>
            <p>{t('about.impact.savings.description')}</p>
          </div>
          
          <div className="stat-item">
            <span className="stat-icon">🤝</span>
            <h3>{t('about.impact.connections.title')}</h3>
            <p>{t('about.impact.connections.description')}</p>
          </div>
        </div>
      </div>

      {/* Creator Section */}
      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">{t('about.creator.title')}</h2>
        </div>
        <div className="creator-content">
          <div className="creator-info">
            <div className="creator-avatar">
              <span className="avatar-text">DK</span>
            </div>
            <div className="creator-details">
              <h3 className="creator-name">{t('about.creator.name')}</h3>
              <p className="creator-role">{t('about.creator.role')}</p>
              <p className="creator-description">
                {t('about.creator.description')}
              </p>
              <div className="creator-passion">
                <h4>{t('about.creator.passion.title')}</h4>
                <p>{t('about.creator.passion.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Technology Section */}
      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">{t('about.technology.title')}</h2>
          <p className="section-subtitle">{t('about.technology.subtitle')}</p>
        </div>
        <div className="tech-features">
          <div className="tech-item">
            <span className="tech-icon">📱</span>
            <h3>{t('about.technology.realtime.title')}</h3>
            <p>{t('about.technology.realtime.description')}</p>
          </div>
          
          <div className="tech-item">
            <span className="tech-icon">🗺️</span>
            <h3>{t('about.technology.routing.title')}</h3>
            <p>{t('about.technology.routing.description')}</p>
          </div>
          
          <div className="tech-item">
            <span className="tech-icon">🔐</span>
            <h3>{t('about.technology.security.title')}</h3>
            <p>{t('about.technology.security.description')}</p>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">{t('about.cta.title')}</h2>
          <p className="cta-description">{t('about.cta.description')}</p>
          <div className="cta-buttons">
            <a href="/login" className="cta-button primary">
              {t('about.cta.join_button')}
            </a>
            <a href="/contact" className="cta-button secondary">
              {t('about.cta.contact_button')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;